import * as WebBrowser from 'expo-web-browser';
import { Platform, StyleSheet, Text, View, type TextProps } from 'react-native';

import { PrimaryButton } from '@/components/buttons';
import { LegalPage, Mail, P, Section } from '@/components/legal-page';
import { SITE_NAME, SUPPORT_FORM_URL } from '@/constants/site';
import { colors, radius, type } from '@/theme';

/**
 * The support URL given to App Store Connect and Google Play.
 *
 * Messages go to a Google Form rather than an in-app form: there is no backend
 * to receive a POST, and a form that silently drops what people type is worse
 * than an honest handoff. `SUPPORT_FORM_URL` is the one place the link lives.
 */

/**
 * Two renderings of the same button. On web it must be a real `<a>` — the link
 * is the entire point of this page, and it should survive with JS disabled, be
 * copyable, and be followable by a store reviewer without hydration. On native
 * `openBrowserAsync` presents the form in an in-app browser, which keeps people
 * from being thrown out of the game to fill it in.
 */
function FormButton() {
  const label = 'OPEN THE SUPPORT FORM';

  if (Platform.OS === 'web') {
    const anchor = {
      href: SUPPORT_FORM_URL,
      hrefAttrs: { target: '_blank', rel: 'noopener noreferrer' },
    } as unknown as TextProps;
    return (
      <Text {...anchor} style={styles.linkButton}>
        {label}
      </Text>
    );
  }

  return (
    <PrimaryButton
      label={label}
      onPress={() => {
        WebBrowser.openBrowserAsync(SUPPORT_FORM_URL).catch(() => {});
      }}
    />
  );
}

export default function SupportScreen() {
  return (
    <LegalPage
      kicker="HELP"
      title="Support"
      subtitle="Bugs, questions, roster suggestions — all welcome."
      description={`Get help with ${SITE_NAME}: report a bug, ask a question, or suggest someone for the roster.`}
      current="/support">
      <Section title="Send us a message">
        <P>
          Everything goes through one short form. Tell us what happened, and add your device and OS
          version if something is broken.
        </P>
        <View style={styles.cta}>
          <FormButton />
          <Text style={styles.ctaNote}>
            Opens a Google Form. Prefer email? <Mail subject="1-T support" />
          </Text>
        </View>
      </Section>

      <Section title="Common questions">
        <P>
          <Text style={styles.q}>My draft disappeared when I closed the app. </Text>
          That&apos;s by design — a run lives for one session only. There are no saved games and no
          progress to lose; every draft starts fresh.
        </P>
        <P>
          <Text style={styles.q}>How is the valuation calculated? </Text>
          From how well each pick fits its seat and how the six of them get along, with a deliberate
          streak of luck on top. The exact weights stay unpublished — knowing them would spoil the
          game.
        </P>
        <P>
          <Text style={styles.q}>Someone&apos;s missing from the roster. </Text>
          Probably! Send a name and why they belong, and they may turn up in a future cohort.
        </P>
        <P>
          <Text style={styles.q}>How do I delete my data? </Text>
          Delete the app. Nothing about you is stored on a server — see the privacy policy for the
          full picture.
        </P>
      </Section>
    </LegalPage>
  );
}

const styles = StyleSheet.create({
  q: {
    ...type.body,
    fontWeight: '700',
    color: colors.text,
  },
  cta: {
    gap: 12,
    marginTop: 4,
  },
  /**
   * Matches `PrimaryButton`, but on a Text so react-native-web can render it as
   * an anchor. `display: 'flex'` is what keeps the padding and centring from
   * collapsing — web Text is inline by default.
   */
  linkButton: {
    ...type.label,
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: colors.text,
    borderRadius: radius.pill,
    paddingVertical: 18,
    fontSize: 13,
    color: colors.bg,
    textAlign: 'center',
    textDecorationLine: 'none',
  },
  ctaNote: {
    ...type.body,
    fontSize: 13,
    lineHeight: 20,
    color: colors.faint,
    textAlign: 'center',
  },
});
